import { useState, useEffect, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Upload, X, Image as ImageIcon, Film,
  Wand2, Grid3X3, Check, AlertTriangle, ArrowDown, Info, Type, Video,
} from 'lucide-react';
import type { WizardMode, CreativeTab, AspectRatio, AiGeneration } from '@/stores/campaignWizardStore';
import { useWizardStore } from '@/stores/campaignWizardStore';
import { getCampaignTypeByKey, campaignTypeSupportsVideo } from '@/lib/campaignTypeRegistry';
import { ADMIN_AI_ENABLED } from '@/lib/featureFlags';
import PinStylePicker from './PinStylePicker';
import { BrandLogoUploader } from '@/components/BrandLogoUploader';
import { adminApi } from '@/lib/adminApi';
import { optimizeImageForUpload, PHOTO_PRESET } from '@/lib/imageOptimize';
import { deleteSponsorCreativeByUrl } from '@/lib/storageCleanup';
import { COPY_LIMITS, creativeCopyIssues } from '@/lib/creativeCopy';

/** Character counter that turns amber past the recommended length and red past the max. */
function CharCount({ value, recommended, max }: { value: string; recommended: number; max: number }) {
  const n = value.trim().length;
  const tone = n > max ? 'text-red-400' : n > recommended ? 'text-amber-300' : 'text-gray-500';
  return <div className={`text-[11px] text-right ${tone}`}>{n}/{recommended}</div>;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const MAX_IMAGE_SIZE = 8 * 1024 * 1024;
const MAX_VIDEO_SIZE = 25 * 1024 * 1024; // matches the sponsor-creatives bucket limit
const MAX_VIDEO_SECONDS = 10; // short loops only — protects the passenger experience + data
const ACCEPTED_IMAGE_MIME: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};
const ACCEPTED_VIDEO_MIME: Record<string, string[]> = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
};

const ASPECT_RATIOS: { value: AspectRatio; label: string; desc: string }[] = [
  { value: 'banner', label: 'Banner', desc: '16:9 wide' },
  { value: 'square', label: 'Square', desc: '1:1' },
  { value: 'tall', label: 'Tall', desc: '9:16 portrait' },
];

/**
 * Read the natural width/height of an image File and return its aspect
 * ratio (width/height). Resolves with -1 if the file can't be decoded
 * (e.g. corrupt, unsupported format).
 */
function readImageAspect(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.width === 0 ? -1 : img.width / img.height);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(-1);
    };
    img.src = url;
  });
}

/**
 * Read a video File's duration in seconds. Resolves with -1 if the file
 * can't be decoded so callers can decide whether to block or allow.
 */
function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(v.duration) ? v.duration : -1);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(-1);
    };
    v.src = url;
  });
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface CreativeStudioProps {
  mode: WizardMode;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function CreativeStudio({ mode }: CreativeStudioProps) {
  const store = useWizardStore(mode);
  const { creativeTab } = store;

  const tabs: { key: CreativeTab; label: string; icon: React.ReactNode }[] = [
    {
      key: 'ai',
      label: ADMIN_AI_ENABLED ? 'AI-generated' : 'Write copy',
      icon: ADMIN_AI_ENABLED ? <Sparkles className="w-3.5 h-3.5" /> : <Type className="w-3.5 h-3.5" />,
    },
    { key: 'upload', label: 'Upload your own', icon: <Upload className="w-3.5 h-3.5" /> },
    { key: 'gallery', label: 'Logo gallery', icon: <Grid3X3 className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-white">Creative</h2>

      {/* Map-pin style picker — only renders when the operator has the
          Sponsored Map Pin template selected. Hidden otherwise so the
          step stays uncluttered for banner / takeover campaigns. */}
      <PinStylePicker mode={mode} />

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-800/50 rounded-lg p-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex-1 justify-center ${
              creativeTab === t.key
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => store.set({ creativeTab: t.key })}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {creativeTab === 'ai' && <AiTab mode={mode} />}
      {creativeTab === 'upload' && <UploadTab mode={mode} />}
      {creativeTab === 'gallery' && <GalleryTab mode={mode} />}

      {/* Deal code: shown to riders who tap the ad, for every creative tab. */}
      <div className="space-y-2 border-t border-gray-800 pt-4">
        <p className="text-xs uppercase tracking-wider text-gray-500">Deal (optional)</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            value={store.offerCode}
            onChange={(e) =>
              store.set({ offerCode: e.target.value.toUpperCase().replace(/\s+/g, '').slice(0, 24) })
            }
            className="bg-gray-900 border-gray-700 text-white font-mono"
            placeholder="Deal code, optional (e.g. HPLATE10)"
          />
          <Input
            value={store.offerTerms}
            onChange={(e) => store.set({ offerTerms: e.target.value.slice(0, 200) })}
            className="bg-gray-900 border-gray-700 text-white"
            placeholder="Deal terms (e.g. 10% off mains, until Oct 31)"
          />
        </div>
        {store.offerCode ? (
          <p className="text-[11px] text-gray-500">
            Riders see this code when they tap the ad and show it at the counter. Ask the business how many they took; that number sells the renewal.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ─── AI Tab ─────────────────────────────────────────────────────────────────

function AiTab({ mode }: { mode: WizardMode }) {
  const store = useWizardStore(mode);
  const {
    brandBrief, headline, body, ctaText, backgroundColor,
    aiCopyLoading, artStyleHint, artAspectRatio, artLoading, artImageUrl,
    recentGenerations, selectedBusiness, templateKey,
  } = store;

  // Anchor we scroll to after a successful copy generation so the operator
  // can see the headline / body / CTA outputs immediately. Without this they
  // think nothing happened because the output lands further down the page.
  const outputAnchorRef = useRef<HTMLDivElement>(null);
  // Tracks whether copy has been successfully generated at least once in
  // this wizard session — used to show an "Output" pill in the header so
  // the relationship between input and output is obvious.
  const hasGeneratedCopy = headline.length > 0 || body.length > 0;
  const copyIssues = creativeCopyIssues({ headline, body, ctaText });

  // Compose a sensible fallback brief from the business profile when the
  // operator hasn't typed anything. This gives the AI useful context even
  // for legacy directory entries that have no description column.
  const fallbackBrief = (() => {
    if (!selectedBusiness) return '';
    const bits: string[] = [];
    if (selectedBusiness.description) bits.push(selectedBusiness.description);
    if (selectedBusiness.slogan) bits.push(`Tagline: ${selectedBusiness.slogan}`);
    if (selectedBusiness.category)
      bits.push(`Category: ${selectedBusiness.category.replace(/_/g, ' ')}`);
    if (selectedBusiness.village)
      bits.push(`Located in ${selectedBusiness.village}${selectedBusiness.parish ? `, ${selectedBusiness.parish}` : ''}`);
    return bits.join('. ');
  })();

  const generateCopy = async () => {
    if (!selectedBusiness) { toast.error('Select a business first'); return; }
    store.set({ aiCopyLoading: true });
    try {
      const { data, error } = await supabase.functions.invoke('generate-sponsor-creative', {
        body: {
          template_key: templateKey || 'hotel_monthly',
          brand_name: selectedBusiness.name,
          brand_description:
            brandBrief?.trim() ||
            fallbackBrief ||
            selectedBusiness.description ||
            '',
          brand_category: selectedBusiness.category || 'other',
        },
      });
      if (error) throw error;
      if (data) {
        store.set({
          headline: data.headline || headline,
          body: data.body || body,
          ctaText: data.cta_text || ctaText,
          backgroundColor: data.background_color || backgroundColor,
        });
        if (data.ai_used === false) {
          toast.info('The AI writer is offline, so we filled in starter copy. Please read it and make it specific to this business.');
        } else {
          toast.success('AI copy generated. Check the headline below.');
        }
        // Smoothly bring the operator's eyes to the result so they don't
        // think the click did nothing.
        setTimeout(() => {
          outputAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } catch (err: any) {
      toast.error(err.message || 'Copy generation failed');
    } finally {
      store.set({ aiCopyLoading: false });
    }
  };

  const generateArt = async () => {
    if (!selectedBusiness) { toast.error('Select a business first'); return; }
    store.set({ artLoading: true });
    try {
      const { data, error } = await supabase.functions.invoke('generate-banner-art', {
        body: {
          template_key: templateKey || 'hotel_monthly',
          brand_name: selectedBusiness.name,
          brand_description: brandBrief || selectedBusiness.description || '',
          headline: headline || '',
          style_hint: artStyleHint || '',
          aspect_ratio: artAspectRatio,
        },
      });
      if (error) throw error;
      if (data?.ok && data.image_url) {
        const gen: AiGeneration = {
          id: data.generation_id || `gen-${Date.now()}`,
          imageUrl: data.image_url,
          prompt: artStyleHint || 'Default style',
          aspectRatio: artAspectRatio,
          timestamp: Date.now(),
        };
        store.addGeneration(gen);
        toast.success('Banner art generated');
      } else {
        throw new Error(data?.error || 'Art generation failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Art generation failed');
    } finally {
      store.set({ artLoading: false });
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Copy generation (AI) — only rendered when AI is enabled. The
         editable Headline / Body / CTA fields lower down are always
         available so operators can write copy by hand. */}
      {ADMIN_AI_ENABLED && (
      <div className="space-y-3 bg-purple-500/[0.04] border border-purple-500/20 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <Type className="w-3.5 h-3.5 text-purple-300" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">AI Copywriter</h3>
            <p className="text-[11px] text-gray-400">
              Write a short brief about the deal. The AI generates a headline, body and CTA from it.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Label className="text-gray-300 text-xs font-medium uppercase tracking-wide flex items-center gap-1.5">
            <span>Brief</span>
            <span className="text-purple-400/60 text-[10px] normal-case tracking-normal">(input)</span>
          </Label>
          <span className="text-xs text-gray-500">{(brandBrief || '').length}/240</span>
        </div>
        <Textarea
          value={brandBrief}
          onChange={(e) => store.set({ brandBrief: e.target.value.slice(0, 240) })}
          className="bg-gray-900 border-gray-700 text-white min-h-[70px]"
          placeholder={
            fallbackBrief
              ? `Empty? We'll auto-use: "${fallbackBrief.slice(0, 100)}${fallbackBrief.length > 100 ? '…' : ''}"`
              : 'Tell the AI what to highlight, e.g. "Free hotel shuttle from cruise port, 20% off lunch buffet."'
          }
        />

        {!brandBrief?.trim() && fallbackBrief && (
          <div className="flex items-start gap-1.5 text-[11px] text-gray-500">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              Brief is empty, so we'll automatically use the business profile
              ({selectedBusiness?.category ? selectedBusiness.category.replace(/_/g, ' ') : 'directory entry'},
              tagline, location). Type to override.
            </span>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <Button
            onClick={generateCopy}
            disabled={aiCopyLoading || !selectedBusiness}
            className="bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-900/30"
          >
            {aiCopyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {aiCopyLoading ? 'Writing copy (~6s)…' : (hasGeneratedCopy ? 'Regenerate copy' : 'Generate copy')}
          </Button>
          {hasGeneratedCopy && (
            <button
              onClick={() => outputAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="text-[11px] text-purple-300 hover:text-purple-200 flex items-center gap-1"
              title="Jump to generated headline / body / CTA"
            >
              <ArrowDown className="w-3 h-3" />
              See output below
            </button>
          )}
        </div>
      </div>
      )}

      {/* Banner art generation (AI) — only rendered when AI is enabled. */}
      {ADMIN_AI_ENABLED && (
      <div className="space-y-3 bg-amber-500/[0.03] border border-amber-500/20 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Wand2 className="w-3.5 h-3.5 text-amber-300" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">AI Banner Art</h3>
            <p className="text-[11px] text-gray-400">
              Generate a custom background image for the ad with DALL·E. Skip this if you're using a logo.
            </p>
          </div>
        </div>
        <Label className="text-gray-300 text-xs font-medium uppercase tracking-wide">
          Style hint <span className="text-amber-400/60 text-[10px] normal-case tracking-normal ml-1">(optional)</span>
        </Label>
        <Textarea
          value={artStyleHint}
          onChange={(e) => store.set({ artStyleHint: e.target.value })}
          className="bg-gray-900 border-gray-700 text-white min-h-[60px]"
          placeholder="Style hint: e.g. Photoreal hotel exterior, golden hour, ocean horizon"
        />
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Aspect:</span>
          {ASPECT_RATIOS.map((ar) => (
            <button
              key={ar.value}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                artAspectRatio === ar.value
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
              }`}
              onClick={() => store.set({ artAspectRatio: ar.value })}
            >
              {ar.label}
            </button>
          ))}
        </div>
        <Button
          onClick={generateArt}
          disabled={artLoading}
          className="bg-amber-600 hover:bg-amber-500 text-white"
        >
          {artLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
          {artLoading ? 'Generating (~10s)…' : 'Generate art'}
        </Button>

        {/* Current art preview */}
        {artImageUrl && (
          <div className="relative">
            <img src={artImageUrl} alt="Generated banner" className="w-full rounded-lg border border-gray-700" />
            <button
              className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gray-900/80 flex items-center justify-center hover:bg-red-500/80 transition-colors"
              onClick={() => store.set({ artImageUrl: '' })}
            >
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        )}

        {/* Recent generations strip */}
        {recentGenerations.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-xs text-gray-500">Recent generations</span>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {recentGenerations.map((g) => (
                <button
                  key={g.id}
                  className={`shrink-0 w-20 h-14 rounded-md overflow-hidden border-2 transition-colors ${
                    artImageUrl === g.imageUrl ? 'border-emerald-500' : 'border-gray-700 hover:border-gray-500'
                  }`}
                  onClick={() => store.set({ artImageUrl: g.imageUrl })}
                >
                  <img src={g.imageUrl} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Editable output fields — these are filled by "Generate copy" above
          and can also be edited freehand. The header pill shows the
          input → output relationship so the operator never wonders where
          AI results landed. */}
      <div ref={outputAnchorRef} className="space-y-3 border-t border-gray-800 pt-5 scroll-mt-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-gray-300 font-medium">Headline · Body · CTA</Label>
            <span className="text-[10px] uppercase tracking-wider text-emerald-400/80 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded">
              {ADMIN_AI_ENABLED && hasGeneratedCopy ? 'AI output' : 'Copy'}
            </span>
          </div>
          <span className="text-[11px] text-gray-500">Editable. Fine-tune anything before publish.</span>
        </div>
        <div className="space-y-1">
          <Input
            value={headline}
            onChange={(e) => store.set({ headline: e.target.value })}
            className="bg-gray-900 border-gray-700 text-white"
            placeholder="Headline, e.g. Fresh from the farm today"
          />
          <CharCount value={headline} recommended={COPY_LIMITS.headline.recommended} max={COPY_LIMITS.headline.max} />
        </div>
        <div className="space-y-1">
          <Textarea
            value={body}
            onChange={(e) => store.set({ body: e.target.value })}
            className="bg-gray-900 border-gray-700 text-white min-h-[60px]"
            placeholder="One line about the offer, e.g. Local mangoes, a short walk from the bus stand."
          />
          <CharCount value={body} recommended={COPY_LIMITS.body.recommended} max={COPY_LIMITS.body.max} />
        </div>
        {copyIssues.length > 0 && headline.trim().length > 0 && (
          <ul className="text-xs text-amber-300 space-y-0.5">
            {copyIssues.map((issue) => (
              <li key={issue} className="flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                {issue}
              </li>
            ))}
          </ul>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input
            value={ctaText}
            onChange={(e) => store.set({ ctaText: e.target.value })}
            className="bg-gray-900 border-gray-700 text-white"
            placeholder="Button text (e.g. See the deals)"
          />
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => store.set({ backgroundColor: e.target.value })}
              className="w-9 h-9 rounded-lg border border-gray-700 cursor-pointer bg-transparent shrink-0"
            />
            <Input
              value={backgroundColor}
              onChange={(e) => store.set({ backgroundColor: e.target.value })}
              className="bg-gray-900 border-gray-700 text-white font-mono text-sm"
              placeholder="#0f3d2e"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Upload Tab ─────────────────────────────────────────────────────────────

function UploadTab({ mode }: { mode: WizardMode }) {
  const store = useWizardStore(mode);
  const { uploadImageUrl, uploadVideoUrl, clientSubOption, campaignType } = store;
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [aspectWarning, setAspectWarning] = useState<string | null>(null);

  // The recommended aspect for the selected campaign type
  const typeDef = campaignType ? getCampaignTypeByKey(campaignType) : undefined;

  // Video only actually plays on premium full-screen surfaces
  // (ride-complete + festival). Everywhere else the app downgrades it to a
  // still image, so we hide the "Custom video" option entirely to keep the
  // wizard simple and avoid an operator uploading a video that never plays.
  const allowVideo = campaignTypeSupportsVideo(typeDef);

  // If the campaign type doesn't support video but a stale "video"
  // sub-option is selected (e.g. operator switched campaign type), fall
  // back to the logo option so we never leave the studio stuck on a
  // hidden panel.
  useEffect(() => {
    if (!allowVideo && clientSubOption === 'video') {
      store.set({ clientSubOption: 'logo', uploadVideoUrl: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowVideo, clientSubOption]);

  const uploadFile = async (file: File, type: 'image' | 'video') => {
    const setter = type === 'image' ? setImageUploading : setVideoUploading;
    setter(true);
    setAspectWarning(null);
    try {
      // Duration guardrail for videos — short loops only. This protects
      // passengers (no long autoplaying ads) and keeps mobile data use
      // sane in Dominica. A `return` here still runs the finally below.
      if (type === 'video') {
        const dur = await readVideoDuration(file);
        if (dur > 0 && dur > MAX_VIDEO_SECONDS + 0.5) {
          toast.error(
            `This clip is ${Math.round(dur)}s. Please trim it to ${MAX_VIDEO_SECONDS} seconds or less before uploading.`,
          );
          return;
        }
      }

      // Aspect-ratio guardrail for images
      if (type === 'image' && typeDef) {
        const ratio = await readImageAspect(file);
        if (ratio > 0) {
          if (ratio < typeDef.aspectMin || ratio > typeDef.aspectMax) {
            setAspectWarning(
              `This image is ${ratio.toFixed(2)}:1, but ${typeDef.label} expects ${typeDef.aspectRatioLabel} (${typeDef.aspectMin.toFixed(2)}:1–${typeDef.aspectMax.toFixed(2)}:1). It may render with bars or get cropped on phones.`,
            );
          }
        }
      }

      const upload = type === 'image' ? await optimizeImageForUpload(file, PHOTO_PRESET) : file;

      // Read the bytes up front. Supabase Storage rejects an empty body with
      // the cryptic "No content provided" error, and storage-js wraps a
      // File/Blob in multipart FormData — so an UNREADABLE file (a macOS
      // Photos / iCloud "optimized" placeholder that hasn't downloaded)
      // sails past a size check yet still produces an empty/garbled part.
      // Reading the ArrayBuffer here (a) forces the real bytes to load, so
      // an unreadable file throws/returns empty and we catch it, and (b)
      // uploads a raw buffer instead of FormData, which is the only path
      // where our contentType is actually honoured.
      let bytes: ArrayBuffer;
      try {
        bytes = await upload.arrayBuffer();
      } catch {
        bytes = new ArrayBuffer(0);
      }
      if (!bytes.byteLength) {
        toast.error(
          type === 'video'
            ? 'That video came through empty or unreadable. If it lives in Photos or iCloud, export/download it to your Downloads folder first, then upload that copy.'
            : 'That image came through empty or unreadable. Try re-saving it, then upload again.',
        );
        return;
      }

      const ext = upload.name.split('.').pop() || (type === 'image' ? 'png' : 'mp4');
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      // Some phone-exported .mov/.mp4 files report an empty MIME type; fall
      // back so Storage still stores it with a sensible content type.
      const contentType =
        upload.type || (type === 'video' ? 'video/mp4' : 'image/png');
      const { error: uploadErr } = await supabase.storage
        .from('sponsor-creatives')
        .upload(path, bytes, {
          contentType,
          upsert: false,
        });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage
        .from('sponsor-creatives')
        .getPublicUrl(path);
      // Reclaim the previous creative we uploaded THIS session — nothing is
      // persisted until Publish, so a superseded session upload is always
      // safe to delete and would otherwise orphan in the bucket.
      const prevUrl = type === 'image' ? uploadImageUrl : uploadVideoUrl;
      if (prevUrl && prevUrl !== urlData.publicUrl) {
        void deleteSponsorCreativeByUrl(prevUrl);
      }
      if (type === 'image') {
        store.set({ uploadImageUrl: urlData.publicUrl });
      } else {
        store.set({ uploadVideoUrl: urlData.publicUrl });
      }
      toast.success(`${type === 'image' ? 'Image' : 'Video'} uploaded`);
    } catch (err: any) {
      const raw = String(err?.message || '');
      if (/no content provided/i.test(raw)) {
        toast.error(
          'Upload failed: the file arrived empty. If it came from Photos or iCloud, export it to your device first, then upload that copy.',
        );
      } else if (/exceeded the maximum allowed size|payload too large/i.test(raw)) {
        toast.error('That file is too large for the upload limit. Use a smaller clip.');
      } else {
        toast.error(raw || 'Upload failed');
      }
    } finally {
      setter(false);
    }
  };

  // Map sub-option to creative mode. An uploaded still image ALWAYS
  // publishes as full-bleed — the app only paints creative images on
  // full-bleed / video surfaces, so we never produce 'client_image'
  // (which the app silently drops, hiding the sponsor's artwork).
  useEffect(() => {
    const modeMap: Record<string, string> = {
      logo: 'admin_authored',
      full_bleed: 'client_full_bleed',
      video: 'client_video',
    };
    const cm = modeMap[clientSubOption] || 'admin_authored';
    if (clientSubOption === 'full_bleed' && uploadImageUrl) {
      store.set({ creativeMode: 'client_full_bleed' });
    } else if (clientSubOption === 'video' && uploadVideoUrl) {
      store.set({ creativeMode: 'client_video' });
    } else {
      store.set({ creativeMode: cm as any });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSubOption, uploadImageUrl, uploadVideoUrl]);

  const subOptions: {
    key: string;
    label: string;
    desc: string;
    bestFor: string;
    icon: React.ReactNode;
  }[] = [
    {
      key: 'logo',
      label: 'Logo + text',
      desc: 'Use the brand logo on a solid color background, with a headline & CTA you write on top.',
      bestFor: 'Quickest. Works for any verified directory brand.',
      icon: <Sparkles className="w-4 h-4 text-emerald-300" />,
    },
    {
      key: 'full_bleed',
      label: 'Custom image (full-bleed)',
      desc: 'Use a finished marketing photo from the client. We overlay headline & CTA on top.',
      bestFor: 'Hotels & restaurants with strong photography.',
      icon: <ImageIcon className="w-4 h-4 text-cyan-300" />,
    },
    // Video is only offered on placements where it actually plays
    // (ride-complete + festival takeover). See campaignTypeSupportsVideo.
    ...(allowVideo
      ? [
          {
            key: 'video',
            label: 'Custom video',
            desc: 'Use a short video from the client (10 seconds or less). Plays muted and autoloops. MP4 preferred; MOV works too.',
            bestFor: 'Premium festival or ride-complete campaigns. Max 25 MB.',
            icon: <Video className="w-4 h-4 text-purple-300" />,
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-4">
      {/* Creative mode toggles */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-gray-300 font-medium">How should this ad look?</Label>
          <span className="text-[11px] text-gray-500">Pick one. You can change it later.</span>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {subOptions.map((opt) => {
            const selected = clientSubOption === opt.key;
            return (
              <button
                key={opt.key}
                className={`text-left p-3 rounded-lg border transition-colors ${
                  selected
                    ? 'bg-emerald-500/10 border-emerald-500/40 ring-1 ring-emerald-500/20'
                    : 'bg-gray-800/30 border-gray-700 hover:border-gray-600'
                }`}
                onClick={() => {
                  const patch: Record<string, unknown> = { clientSubOption: opt.key };
                  // Drop a stale upload from another mode so it can't
                  // override the new choice at publish time.
                  if (opt.key !== 'full_bleed') patch.uploadImageUrl = '';
                  if (opt.key !== 'video') patch.uploadVideoUrl = '';
                  store.set(patch as any);
                }}
              >
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                    selected ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-gray-700/50 border border-gray-700'
                  }`}>
                    {selected ? <Check className="w-4 h-4 text-emerald-400" /> : opt.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span
                      className={`text-sm font-semibold block ${
                        selected ? 'text-emerald-300' : 'text-white'
                      }`}
                    >
                      {opt.label}
                    </span>
                    <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                    <p className="text-[11px] text-gray-500 mt-1 italic">Best for: {opt.bestFor}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Aspect ratio guidance from selected type */}
      {typeDef && clientSubOption === 'full_bleed' && (
        <div className="text-[11px] text-gray-400 bg-gray-800/40 border border-gray-700/50 rounded-md p-2.5 flex items-start gap-2">
          <ImageIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-cyan-400" />
          <div>
            <span className="text-gray-300 font-medium">{typeDef.label}</span>{' '}
            uses <span className="text-cyan-300 font-mono">{typeDef.aspectRatioLabel}</span>.
            We'll warn you if your image is the wrong shape.
          </div>
        </div>
      )}

      {/* Image dropzone — only for the custom full-bleed image option.
          Logo mode uses the brand logo; video mode uses the video zone. */}
      {clientSubOption === 'full_bleed' && (
        <div className="space-y-2">
          <Label className="text-gray-300 flex items-center gap-2">
            <ImageIcon className="w-4 h-4" /> Creative image
          </Label>
          {uploadImageUrl ? (
            <div className="relative">
              <img
                src={uploadImageUrl}
                alt=""
                className="w-full max-h-56 object-contain rounded-lg border border-gray-700 bg-gray-900"
              />
              <button
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500/90 flex items-center justify-center hover:bg-red-500"
                onClick={() => {
                  store.set({ uploadImageUrl: '' });
                  setAspectWarning(null);
                }}
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          ) : (
            <ImageDropZone
              uploading={imageUploading}
              onFile={(f) => uploadFile(f, 'image')}
              hint={typeDef?.aspectRatioLabel}
            />
          )}
          {aspectWarning && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-2.5 text-[11px] text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>{aspectWarning}</div>
            </div>
          )}
        </div>
      )}

      {/* Video dropzone */}
      {clientSubOption === 'video' && (
        <div className="space-y-2">
          <Label className="text-gray-300 flex items-center gap-2">
            <Film className="w-4 h-4" /> Video creative
          </Label>
          {uploadVideoUrl ? (
            <div className="relative">
              <video
                src={uploadVideoUrl}
                controls
                className="w-full max-h-56 rounded-lg border border-gray-700"
              />
              <button
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500/90 flex items-center justify-center hover:bg-red-500"
                onClick={() => store.set({ uploadVideoUrl: '' })}
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          ) : (
            <VideoDropZone
              uploading={videoUploading}
              onFile={(f) => uploadFile(f, 'video')}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Dropzone subcomponents ────────────────────────────────────────────────

function ImageDropZone({
  uploading,
  onFile,
  hint,
}: {
  uploading: boolean;
  onFile: (f: File) => void;
  hint?: string;
}) {
  const onDrop = useCallback(
    (accepted: File[]) => {
      const file = accepted[0];
      if (!file) return;
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error('Image must be under 8 MB');
        return;
      }
      onFile(file);
    },
    [onFile],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    onDropRejected: (rejections) => {
      const err = rejections[0]?.errors?.[0];
      if (err?.code === 'file-too-large') toast.error('Image is too large. Please use a file under 8 MB.');
      else if (err?.code === 'file-invalid-type') toast.error('Unsupported file. Please upload a PNG, JPG, or WebP.');
      else toast.error(err?.message || 'That image could not be used. Try a PNG/JPG under 8 MB.');
    },
    accept: ACCEPTED_IMAGE_MIME,
    maxFiles: 1,
    maxSize: MAX_IMAGE_SIZE,
    multiple: false,
    disabled: uploading,
  });

  const borderClass = isDragReject
    ? 'border-red-500/60 bg-red-500/5'
    : isDragActive
    ? 'border-emerald-400 bg-emerald-500/10 scale-[1.01]'
    : 'border-gray-600 hover:border-emerald-500/50 hover:bg-emerald-500/5';

  return (
    <div
      {...getRootProps()}
      className={`block w-full min-h-[120px] rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 flex items-center justify-center ${borderClass}`}
    >
      <input {...getInputProps()} />
      {uploading ? (
        <div className="flex flex-col items-center text-emerald-400">
          <Loader2 className="w-7 h-7 animate-spin" />
          <span className="text-xs mt-1.5">Uploading…</span>
        </div>
      ) : isDragActive ? (
        <div className="flex flex-col items-center text-emerald-300">
          <Upload className="w-7 h-7" />
          <span className="text-xs mt-1.5 font-semibold">Drop the file</span>
        </div>
      ) : (
        <div className="flex flex-col items-center text-gray-400">
          <Upload className="w-6 h-6 text-gray-500" />
          <span className="text-sm mt-2 text-gray-300">
            Drop an image, or <span className="text-emerald-400 underline">click to pick</span>
          </span>
          <span className="text-[11px] text-gray-500 mt-1">
            PNG · JPG · WebP, max 8 MB{hint ? ` · ${hint}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}

function VideoDropZone({
  uploading,
  onFile,
}: {
  uploading: boolean;
  onFile: (f: File) => void;
}) {
  const onDrop = useCallback(
    (accepted: File[]) => {
      const file = accepted[0];
      if (!file) return;
      if (file.size > MAX_VIDEO_SIZE) {
        toast.error('Video must be under 25 MB');
        return;
      }
      onFile(file);
    },
    [onFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (rejections) => {
      const err = rejections[0]?.errors?.[0];
      if (err?.code === 'file-too-large') {
        toast.error('Video is too large. Please use a clip under 25 MB (and 10 seconds or less).');
      } else if (err?.code === 'file-invalid-type') {
        toast.error('Unsupported file. Please upload an MP4 (MOV also works, but MP4 plays on the most phones).');
      } else {
        toast.error(err?.message || 'That video could not be used. Try a short MP4 under 25 MB.');
      }
    },
    accept: ACCEPTED_VIDEO_MIME,
    maxFiles: 1,
    maxSize: MAX_VIDEO_SIZE,
    multiple: false,
    disabled: uploading,
  });

  return (
    <div
      {...getRootProps()}
      className={`block w-full min-h-[120px] rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 flex items-center justify-center ${
        isDragActive
          ? 'border-emerald-400 bg-emerald-500/10'
          : 'border-gray-600 hover:border-emerald-500/50 hover:bg-emerald-500/5'
      }`}
    >
      <input {...getInputProps()} />
      {uploading ? (
        <div className="flex flex-col items-center text-emerald-400">
          <Loader2 className="w-7 h-7 animate-spin" />
          <span className="text-xs mt-1.5">Uploading…</span>
        </div>
      ) : (
        <div className="flex flex-col items-center text-gray-400">
          <Film className="w-6 h-6 text-gray-500" />
          <span className="text-sm mt-2 text-gray-300">
            Drop a video, or{' '}
            <span className="text-emerald-400 underline">click to pick</span>
          </span>
          <span className="text-[11px] text-gray-500 mt-1">MP4 or MOV · 10s or less · max 25 MB</span>
        </div>
      )}
    </div>
  );
}

// ─── Logo Gallery Tab ───────────────────────────────────────────────────────

function GalleryTab({ mode }: { mode: WizardMode }) {
  const store = useWizardStore(mode);
  const { selectedBusiness } = store;
  const [logos, setLogos] = useState<{ id: string; name: string; logo_url: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const loadLogos = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('local_business_listings')
        .select('id, name, logo_url')
        .not('logo_url', 'is', null)
        .neq('logo_url', '')
        .order('name')
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
      if (error) throw error;
      setLogos(data || []);
    } catch {
      setLogos([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { loadLogos(); }, [loadLogos]);

  /**
   * Banner shown above the gallery summarizing the selected brand's
   * logo status. Two states:
   *   1. **No logo yet** → amber prompt + Upload button. Uploading here
   *      persists the URL on the brand row + flows it into the wizard
   *      store so the rest of the wizard (LivePreview, pin style
   *      picker, publish step, the in-app pin card) sees the real
   *      logo immediately.
   *   2. **Logo on file** → neutral panel with the current logo +
   *      Replace and Remove actions. Replace re-runs the upload flow
   *      (overwriting `logo_url`); Remove nulls the column on the
   *      brand row so future campaigns fall back to the letter avatar
   *      again (the file in the bucket is left in place — cheap and
   *      avoids stranding URLs that might still be referenced by
   *      published campaigns).
   */
  const [removingLogo, setRemovingLogo] = useState(false);

  async function handleRemoveLogo() {
    if (!selectedBusiness) return;
    if (!window.confirm(
      `Remove the logo for ${selectedBusiness.name}? The brand will fall back to the letter avatar on the in-app pin card and every future campaign until a new logo is uploaded. This does NOT change campaigns that are already published.`,
    )) return;
    setRemovingLogo(true);
    try {
      await adminApi.updateListing(selectedBusiness.id, { logo_url: null });
      store.set({
        selectedBusiness: { ...selectedBusiness, logo_url: null },
        // If this brand's logo was the active gallery pick, clear that
        // pick so the campaign doesn't fail at publish with an empty
        // logo URL — the rest of the wizard will recompute from
        // selectedBusiness.logo_url (now null) and prompt accordingly.
        gallerySelectedLogoUrl:
          store.gallerySelectedLogoUrl === selectedBusiness.logo_url
            ? ''
            : store.gallerySelectedLogoUrl,
      });
      loadLogos();
      toast.success(`Removed logo for ${selectedBusiness.name}`, {
        description: 'Falls back to the letter avatar until a new one is uploaded.',
      });
    } catch (err: any) {
      toast.error(err?.message || 'Could not remove the logo.');
    } finally {
      setRemovingLogo(false);
    }
  }

  const BrandLogoBanner = selectedBusiness ? (
    !selectedBusiness.logo_url ? (
      // ── No logo yet ──
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
          <span className="text-sm font-bold text-amber-300">
            {selectedBusiness.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-amber-100 font-medium truncate">
            {selectedBusiness.name} has no logo yet
          </p>
          <p className="text-[11px] text-amber-200/70">
            Upload one to replace the fallback letter on the in-app pin card and every future campaign.
          </p>
        </div>
        <BrandLogoUploader
          businessId={selectedBusiness.id}
          businessName={selectedBusiness.name}
          currentLogoUrl={selectedBusiness.logo_url}
          onUploaded={(publicUrl) => {
            store.set({
              selectedBusiness: { ...selectedBusiness, logo_url: publicUrl },
              gallerySelectedLogoUrl: publicUrl,
            });
            loadLogos();
          }}
        />
      </div>
    ) : (
      // ── Logo on file ──
      <div className="rounded-xl border border-gray-700 bg-gray-800/40 p-3 flex items-center gap-3">
        <img
          src={selectedBusiness.logo_url}
          alt={`${selectedBusiness.name} logo`}
          className="w-10 h-10 rounded-full object-cover border border-gray-700 shrink-0 bg-white"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-100 font-medium truncate">
            {selectedBusiness.name} · logo on file
          </p>
          <p className="text-[11px] text-gray-400">
            Used as the default logo for this campaign. Replace it to update everywhere this brand renders, or remove it to fall back to the letter avatar.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <BrandLogoUploader
            businessId={selectedBusiness.id}
            businessName={selectedBusiness.name}
            currentLogoUrl={selectedBusiness.logo_url}
            onUploaded={(publicUrl) => {
              store.set({
                selectedBusiness: { ...selectedBusiness, logo_url: publicUrl },
                gallerySelectedLogoUrl: publicUrl,
              });
              loadLogos();
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={removingLogo}
            onClick={handleRemoveLogo}
            className="text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
            title={`Remove the logo for ${selectedBusiness.name}`}
          >
            {removingLogo
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <X className="w-3.5 h-3.5 mr-1.5" />}
            Remove
          </Button>
        </div>
      </div>
    )
  ) : null;

  if (loading) {
    return (
      <div className="space-y-3">
        {BrandLogoBanner}
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (logos.length === 0) {
    return (
      <div className="space-y-3">
        {BrandLogoBanner}
        <div className="text-center py-8 text-gray-500 text-sm">
          No business logos in the directory yet.
        </div>
      </div>
    );
  }

  // The "active" logo for this campaign is whichever the operator most
  // recently clicked from the gallery; if they haven't clicked anything
  // yet we default to the selected brand's own logo. This must be a
  // SINGLE source of truth so the gallery is genuinely single-select —
  // the previous OR'd check left the brand's own tile highlighted on
  // top of whatever the operator picked, which made it look like two
  // brands were selected at once.
  const activeLogoUrl =
    store.gallerySelectedLogoUrl ||
    store.selectedBusiness?.logo_url ||
    '';

  return (
    <div className="space-y-3">
      {BrandLogoBanner}
      <p className="text-sm text-gray-400">Click a logo to use it in this campaign.</p>
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
        {logos.map((l) => {
          const isSelected = !!activeLogoUrl && activeLogoUrl === l.logo_url;
          return (
            <button
              key={l.id}
              className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-colors ${
                isSelected
                  ? 'bg-emerald-500/10 border-emerald-500/30'
                  : 'bg-gray-800/30 border-gray-700/50 hover:border-gray-600'
              }`}
              onClick={() => {
                store.set({ gallerySelectedLogoUrl: l.logo_url });
                toast.success(`Using ${l.name}'s logo`);
              }}
              title={l.name}
            >
              <img
                src={l.logo_url}
                alt={l.name}
                className="w-12 h-12 rounded-full object-cover border border-gray-700"
              />
              <span className="text-[10px] text-gray-400 truncate w-full text-center">{l.name}</span>
              {isSelected && <Check className="w-3 h-3 text-emerald-400" />}
            </button>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          className="text-gray-400 text-xs"
        >
          Previous
        </Button>
        <span className="text-xs text-gray-500">Page {page + 1}</span>
        <Button
          variant="ghost"
          size="sm"
          disabled={logos.length < PAGE_SIZE}
          onClick={() => setPage((p) => p + 1)}
          className="text-gray-400 text-xs"
        >
          Next
        </Button>
      </div>
    </div>
  );
}
